import * as React from 'react'
import { styled } from '@mui/joy/styles'
import { Input, InputProps } from '@mui/joy'
import { globalStyles } from '@styles/styles'

const floatLabelStyles = {
  top: '-0.5rem',
  fontSize: '0.75rem',
  backgroundColor: 'var(--joy-palette-background-surface)',
  zIndex: 1,
  padding: '0 0.25rem',
}

const StyledInput = styled('input')({
  border: 'none',
  minWidth: 0,
  outline: 0,
  padding: 0,
  flex: 1,
  color: 'inherit',
  backgroundColor: 'transparent',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  fontStyle: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
  textOverflow: 'ellipsis',

  '&::placeholder': { opacity: 0, transition: 'opacity 120ms ease-out' },
  '&[data-has-placeholder="true"]::placeholder': { opacity: 0.64 },
  '&:focus::placeholder': { opacity: 1 },

  '&:focus ~ label': floatLabelStyles as any,
  '&[data-has-value="true"] ~ label': floatLabelStyles as any,
  '&[data-has-placeholder="true"] ~ label': floatLabelStyles as any,

  '&:-webkit-autofill ~ label': floatLabelStyles as any,

  '&:-webkit-autofill': { alignSelf: 'stretch' },
  '&:-webkit-autofill:not(* + &)': {
    marginInlineStart: 'calc(-1 * var(--Input-paddingInline))',
    paddingInlineStart: 'var(--Input-paddingInline)',
    borderTopLeftRadius: 'calc(var(--Input-radius) - var(--variant-borderWidth, 0px))',
    borderBottomLeftRadius: 'calc(var(--Input-radius) - var(--variant-borderWidth, 0px))',
  },
})

const StyledLabel = styled('label')<{ hasStartDecorator: boolean }>(({ theme, hasStartDecorator }) => ({
  position: 'absolute',
  lineHeight: 1,
  top: hasStartDecorator ? '-0.5rem' : 'calc((var(--Input-minHeight) - 1em) / 2)',
  left: '0.75rem',
  fontSize: hasStartDecorator ? '0.75rem' : 'inherit',
  backgroundColor: hasStartDecorator ? 'var(--joy-palette-background-surface)' : 'transparent',
  zIndex: hasStartDecorator ? 1 : 'auto',
  padding: hasStartDecorator ? '0 0.25rem' : '0',
  color: theme.vars.palette.text.tertiary,
  fontWeight: theme.vars.fontWeight.md,
  transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
}))

export const InnerInput = React.forwardRef<
  HTMLInputElement,
  JSX.IntrinsicElements['input'] & { label: React.ReactNode; hasStartDecorator: boolean }
>(function InnerInput(props, ref) {
  const { hasStartDecorator, ...inputProps } = props
  const id = React.useId()
  return (
    <React.Fragment>
      <StyledInput
        {...inputProps}
        ref={ref}
        id={id}
      />
      <StyledLabel
        htmlFor={id}
        hasStartDecorator={hasStartDecorator}
      >
        {props.label}
      </StyledLabel>
    </React.Fragment>
  )
})

export const FloatingLabelInput = ({
  innerRef,
  name,
  placeholder,
  onChange,
  disabled,
  value,
  label,
  type,
  onFocus,
  sx,
  startDecorator,
  endDecorator,
  onBlur,
  onKeyDown,
  min,
  max,
  step,
  slotProps,
  readOnly,
}: InputProps & {
  label: React.ReactNode
  innerRef?: ((instance: HTMLInputElement | null) => void) | React.RefObject<HTMLInputElement> | null | undefined
} & React.InputHTMLAttributes<HTMLInputElement>) => (
  <Input
    slotProps={{
      ...slotProps,
      input: {
        type,
        name,
        placeholder,
        onChange,
        disabled,
        value,
        onFocus,
        label,
        sx,
        onBlur,
        ref: innerRef,
        onKeyDown,
        min,
        max,
        step,
        hasStartDecorator: !!startDecorator,
        readOnly,
        'data-has-placeholder': !!placeholder,
        'data-has-value': value != null && String(value).length > 0,
      },
    }}
    slots={{ input: InnerInput }}
    endDecorator={endDecorator}
    startDecorator={startDecorator}
    sx={{
      '--Input-minHeight': `${globalStyles.inputHeight}px`,
      '--Input-radius': '6px',
    }}
  />
)
